import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exportFolderPdf } from '../documentExport';
import { errorMessage } from '../../utils/errorMessage';
import { collectFolderSubtree } from '../documentFolderPdf';
import { readDocument } from '../../extensions/bundled/documents/documentsStore';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * Print / export from a section's row menu: one document's body on demand,
 * and the folder-as-one-PDF flow with its options dialog and progress label.
 */
export default function useSectionExport({ scope, nodes }) {
  const { t } = useTranslation();
  const notes = () => useNotificationStore.getState();
  // Non-null while an export/print is reading documents — these go through IPC
  // and a folder export can take seconds, so the row shows what is happening
  // instead of appearing to have ignored the click.
  const [busy, setBusy] = useState(null);
  // The folder whose PDF options are being chosen: { node, documentCount }.
  const [pdfOptions, setPdfOptions] = useState(null);

  /**
   * Load one document's body on demand.
   *
   * The sidebar tree carries only `{id, name, type}` — bodies are read when a
   * document is OPENED. So exporting or printing from the context menu costs a
   * read that the editor tab's own "…" menu does not, because that tab already
   * holds the body in a ref.
   */
  const withDocumentBody = async (node, run) => {
    setBusy(t('docSidebar.export.loading'));
    try {
      const doc = await readDocument(node.id, scope);
      if (!doc) throw new Error(t('docSidebar.export.readFailed'));
      await run({ title: doc.title || node.name || t('docSidebar.untitled'), bodyHtml: doc.bodyHtml || '' });
    } catch (err) {
      notes().error(errorMessage(err, t('docSidebar.errors.export')), t('docSidebar.export.title'));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Every document in this folder, and its subfolders, as ONE PDF.
   *
   * The reading is the slow part (one IPC round trip per document), so progress
   * is reported per document rather than only at the end — a folder of 60 would
   * otherwise look frozen. `renderFolderPdf` caps the count and returns how many
   * it left out; that number is surfaced here rather than swallowed, because a
   * PDF that is quietly missing documents looks complete.
   */
  /**
   * Ask before exporting.
   *
   * The count comes from `collectFolderSubtree`, which is pure — no documents
   * are read to answer "how many?", so opening the dialog is free. That is also
   * what lets the over-cap warning move to BEFORE the wait instead of after the
   * save dialog.
   */
  const openFolderPdfOptions = (node) => {
    const documentCount = collectFolderSubtree(nodes, node.id).filter(
      (e) => e.type === 'document'
    ).length;
    if (!documentCount) {
      notes().info(t('docSidebar.export.folderEmpty'), t('docSidebar.export.title'));
      return;
    }
    setPdfOptions({ node, documentCount });
  };

  const runFolderPdfExport = async (node, options) => {
    const folderName = node.name || t('docSidebar.export.defaultFolderName');
    setPdfOptions(null);
    setBusy(t('docSidebar.export.preparing'));
    try {
      const { renderFolderPdf } = await import('../documentFolderPdf');
      const { bytes, documentCount, skipped } = await renderFolderPdf({
        nodes,
        folderId: node.id,
        folderName,
        readDocument: (id) => readDocument(id, scope),
        onProgress: (done, total) => setBusy(t('docSidebar.export.reading', { done, total })),
        ...options,
      });
      setBusy(t('docSidebar.export.building'));
      const res = await exportFolderPdf({ folderName, bytes });
      if (res?.saved && skipped > 0) {
        // Repeated after the fact as well as before it: the dialog's warning is
        // easy to click past, and this is the moment the file exists.
        notes().warning(t('docSidebar.export.skipped', { count: documentCount, skipped }), t('docSidebar.export.title'));
      }
    } catch (err) {
      notes().error(errorMessage(err, t('docSidebar.export.pdfFailed')), t('docSidebar.export.title'));
    } finally {
      setBusy(null);
    }
  };

  return { busy, pdfOptions, setPdfOptions, withDocumentBody, openFolderPdfOptions, runFolderPdfExport };
}
